import React, { useState } from 'react';
import { QuestionCard } from './PedagogicalAgent';

/**
 * Short answer card - student types a response, stored for instructor review.
 */
const ShortAnswerCard = ({ item, onSubmitAnswer }) => {
  const [answerText, setAnswerText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!answerText.trim() || submitting) return;
    setSubmitting(true);
    try {
      await onSubmitAnswer(item.question_id, answerText, item.question);
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to submit short answer:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border-2 border-green-300 rounded-lg overflow-hidden bg-green-50/30">
      <div className="bg-green-100 border-b border-green-300 px-4 py-3">
        <span className="text-sm font-semibold text-green-900">Short Answer</span>
      </div>
      <div className="p-4">
        <p className="mb-3 text-gray-900 font-medium text-sm">{item.question}</p>
        {!submitted ? (
          <>
            <textarea
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              placeholder={item.placeholder || 'Type your answer here...'}
              className="w-full border border-gray-300 rounded-md p-2 text-sm mb-3 focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              rows="4"
            />
            <button
              onClick={handleSubmit}
              disabled={!answerText.trim() || submitting}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors text-sm"
            >
              {submitting ? 'Submitting...' : 'Submit Answer'}
            </button>
          </>
        ) : (
          <div className="p-3 bg-green-100 border border-green-300 rounded-lg">
            <p className="text-sm text-green-800 font-medium">Your answer has been submitted for review.</p>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * FeedbackSection - Renders a unified stream of feedback items (text, QA, MCQ, short answer)
 * followed by a rating widget.
 */
const FeedbackSection = ({ erdState }) => {
  const handleFAQClick = (questionIndex, question) => {
    if (erdState.logFAQClick) {
      erdState.logFAQClick(questionIndex, question);
    }
  };

  const items = erdState.feedbackItems || [];

  return (
    <div>
      <h3 className="font-medium text-gray-800 mb-3">Feedback:</h3>

      {/* Render all items in order */}
      <div className="space-y-3 mb-4">
        {items.map((item, index) => {
          switch (item.type) {
            case 'text':
              return (
                <div key={index} className="p-3 bg-gray-50 rounded-md">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {item.content.replace(/'/g, '`')}
                  </p>
                </div>
              );

            case 'qa':
              return (
                <details
                  key={index}
                  className="bg-gray-50 rounded-md"
                  onToggle={(e) => {
                    if (e.target.open) {
                      handleFAQClick(index, item.question);
                    }
                  }}
                >
                  <summary className="p-3 cursor-pointer font-medium text-sm text-gray-700 hover:bg-gray-100 rounded-md">
                    {item.question}
                  </summary>
                  <div className="p-3 pt-0 text-sm text-gray-600 whitespace-pre-wrap">
                    {item.answer}
                  </div>
                </details>
              );

            case 'mcq':
            case 'short_answer':
              // Pedagogical items disabled for now
              return null;

            default:
              return null;
          }
        })}
      </div>

      {/* Rating Section */}
      <div className="border-t pt-4">
        <h3 className="font-medium text-gray-800 mb-3">Rate this feedback:</h3>

        {/* Star Rating */}
        <div className="mb-3">
          <div className="flex gap-1 mb-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => erdState.setRating(star)}
                className={`text-2xl ${erdState.rating >= star ? 'text-yellow-400' : 'text-gray-300'} hover:text-yellow-400 transition-colors`}
              >
                ★
              </button>
            ))}
          </div>
          <p className="text-sm text-gray-600">Selected rating: {'★'.repeat(erdState.rating)}</p>
        </div>

        {/* Comment Textarea */}
        <textarea
          value={erdState.comment}
          onChange={(e) => erdState.setComment(e.target.value)}
          placeholder="Additional comment:"
          className="w-full border border-gray-300 rounded-md p-2 text-sm mb-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          rows="3"
        />

        {/* Submit Rating Button */}
        <button
          onClick={erdState.submitRating}
          disabled={!erdState.feedbackId}
          className="w-full bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white px-4 py-2 rounded-md text-sm transition-colors duration-200"
        >
          Submit rating
        </button>
      </div>
    </div>
  );
};

export default FeedbackSection;
